<script setup lang="ts">
import {
  computed,
} from 'vue';

import type { QueryType } from '@/utils/query';
import type { AssetSearchCriteria } from '@/api';
import {
  EMPTY_ASSET_INGESTION_FILTER,
  EMPTY_ASSET_TYPE_FILTER,
  EMPTY_CUSTOM_METADATA_FILTER,
  EMPTY_HEALTH_STATUS_FILTER,
  EMPTY_OWNER_FILTER,
  EMPTY_PLATFORM_FILTER,
  EMPTY_TAG_FILTER,
  EMPTY_TERM_FILTER,
  EMPTY_USAGE_FILTER,
  filtersValuesToQuery,
  INITIAL_SEARCH_PARAMS,
} from '@/components/catalog/Catalog';
import { BaseSearchFilterDto } from '@/api';
import SearchFilterTreeview from '@/components/SearchFilterTreeview.vue';
import CustomMetadataFilters from '@/components/custom-metadata/CustomMetadataFilters.vue';

interface CatalogFiltersProps {
  searchParams: AssetSearchCriteria,
  filters: BaseSearchFilterDto[]
}

const props = defineProps({
  searchParams: null,
  filters: null
});

const emit = defineEmits(['onFilterChange']);

const filtersType = computed(() => ({
  assetFilterType: props.filters.find((filter) => filter.type === BaseSearchFilterDto.type.ASSET_TYPE) as BaseSearchFilterDto || EMPTY_ASSET_TYPE_FILTER,
  datasource: props.filters.find((filter) => filter.type === BaseSearchFilterDto.type.PLATFORM) as BaseSearchFilterDto || EMPTY_PLATFORM_FILTER,
  healthStatus: props.filters.find((filter) => filter.type === BaseSearchFilterDto.type.HEALTH_STATUS) as BaseSearchFilterDto || EMPTY_HEALTH_STATUS_FILTER,
  usage: props.filters.find((filter) => filter.type === BaseSearchFilterDto.type.USAGE) as BaseSearchFilterDto || EMPTY_USAGE_FILTER,
  tag: props.filters.find((filter) => filter.type === BaseSearchFilterDto.type.TAG) as BaseSearchFilterDto || EMPTY_TAG_FILTER,
  term: props.filters.find((filter) => filter.type === BaseSearchFilterDto.type.TERM) as BaseSearchFilterDto || EMPTY_TERM_FILTER,
  customMetadata: props.filters.filter((filter) => filter.type === BaseSearchFilterDto.type.CUSTOM_METADATA) as BaseSearchFilterDto[] || [EMPTY_CUSTOM_METADATA_FILTER],
  owner: props.filters.find((filter) => filter.type === BaseSearchFilterDto.type.OWNER) as BaseSearchFilterDto || EMPTY_OWNER_FILTER,
  ingestion: props.filters.find((filter) => filter.type === BaseSearchFilterDto.type.ASSET_INGESTION) as BaseSearchFilterDto || EMPTY_ASSET_INGESTION_FILTER,
}));

const filterValues = computed(() => filtersValuesToQuery(props.searchParams));

const filterQuantity = computed(() => {
  const {
    assetFilterType, datasource, healthStatus, usage, tag, term, customMetadataEntry, owner, ingestion,
  } = props.searchParams;

  const quantifiableFilters = {
    assetFilterType,
    datasource,
    healthStatus,
    usage,
    tag,
    term,
    customMetadataEntry,
    owner,
    ingestion,
  };

  return Object.values(quantifiableFilters).reduce((acc, curr) => acc + curr!.length, 0);
});

const setValues = (filter: { [key: string]: QueryType }) => {
  emit('onFilterChange', filter);
};

const resetFilters = () => {
  emit('onFilterChange', INITIAL_SEARCH_PARAMS);
};
</script>

<template lang="pug">
#catalog-filters
  FilterReset(
    :filterQuantity="filterQuantity"
    search-domain="data-catalog"
    @reset="resetFilters" )

  SearchFilterTreeview(
    :filter="filtersType.assetFilterType"
    :value="filterValues.assetFilterType"
    @input="setValues({ assetFilterType: $event })"
    expanded
    data-track="asset-filter-box" )

  SearchFilterTreeview(
    :filter="filtersType.datasource"
    :value="filterValues.datasource"
    @input="setValues({ datasource: $event })"
    has-search
    expanded
    data-track="datasource-filter-box" )

  SearchFilterTreeview(
    :filter="filtersType.healthStatus"
    :value="filterValues.healthStatus"
    @input="setValues({ healthStatus: $event })"
    expanded
    data-track="health-status-filter-box" )

  CustomMetadataFilters(
    :filters="filtersType.customMetadata"
    :value="filterValues.customMetadataEntry"
    @input="setValues({ customMetadataEntry: $event })"
  )

  SearchFilterTreeview(
    :filter="filtersType.usage"
    :value="filterValues.usage"
    @input="setValues({ usage: $event })"
    :tooltip="$t('data-catalog.usage_tooltip')"
    hide-empty
    data-track="usage-filter-box" )

  SearchFilterTreeview(
    :filter="filtersType.owner"
    :value="filterValues.owner"
    @input="setValues({ owner: $event })"
    has-search
    data-track="owner-filter-box" )

  SearchFilterTreeview(
    :filter="filtersType.tag"
    :value="filterValues.tag"
    @input="setValues({ tag: $event })"
    has-search
    data-track="tag-filter-box")

  SearchFilterTreeview(
    :filter="filtersType.term"
    :value="filterValues.term"
    @input="setValues({ term: $event })"
    data-track="term-filter-box"
    has-search )

  SearchFilterTreeview(
    :filter="filtersType.ingestion"
    :value="filterValues.ingestion"
    @input="setValues({ ingestion: $event })"
    data-track="ingestion-filter-box")
</template>
